import { encode } from 'qss'
import { getStorefrontShopObject } from '~/lib/shopify/public-credentials'

import { UnlikelyRequestHeaders } from '@unlikelystudio/commerce-connector'

export const QueryRoutes = {
  CONTACT: 'contact',
  COUNTRIES_LIST: 'countries-input-list',
  GEOLOCATION: 'geolocation',
  RECAPTCHA: 'verify-recaptcha',
  RECENTLY_VIEWED_PRODUCTS: 'get-recently-viewed-products',
  RECOMMENDED_PRODUCTS: 'get-recommended-products',

  // Stock Alert - Swym
  BACK_IN_STOCK: "back-in-stock",


  // Wishlist - Swym
  WISHLIST_PROFILE: "wishlist/wishlist-profile",
  GET_WISHLIST: "wishlist/get-wishlist",
  UPDATE_WISHLIST_PRODUCTS: "wishlist/udpate-wishlist-products",
  CREATE_WISHLIST: "wishlist/create-wishlist",
  GET_PUBLIC_WISHLIST: "wishlist/get-public-wishlist",


  // Shopify
  SHOPIFY_CUSTOMERS_NEWSLETTER: 'customers/newsletter',
  SHOPIFY_CUSTOMERS_UPDATE_LOCALE: 'customers/update-locale',
  SHOPIFY_ORDER_ADMIN: 'orders/get-admin-order',

  // Google
  GOOGLE_RECAPTCHA: 'verify-recaptcha',
} as const

export type QueryRoutes = typeof QueryRoutes[keyof typeof QueryRoutes]

export const QueryMethods = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE',
}

export type QueryMethods = typeof QueryMethods[keyof typeof QueryMethods]

type EndpointSettings = {
  action: QueryRoutes
  method: QueryMethods
}

export type QueryPayload = Record<string, string | object | boolean>

type QueryParams = {
  payload?: QueryPayload
  accessToken?: string
  queryParams?: Record<string, string>
}

type GenerateQueryParams = QueryParams & {
  action: QueryRoutes | string
  method: QueryMethods
}

async function generateQuery({
  action,
  payload,
  accessToken,
  queryParams = null,
  method,
}: GenerateQueryParams) {
  const hasBody = method !== QueryMethods.GET
  const shop = getStorefrontShopObject()

  const req = await fetch(
    `/api/${action}/${queryParams ? `?${encode(queryParams)}` : ``}`,
    {
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && {
          [UnlikelyRequestHeaders.AUTHORIZATION]: accessToken,
          [UnlikelyRequestHeaders.STORE_URL]: shop?.storeUrl,
          [UnlikelyRequestHeaders.STORE_TOKEN]: shop?.storefrontAccessToken,
        }),
      },
      method,
      ...(hasBody && { body: JSON.stringify(payload) }),
    },
  )

  const res = await req.json()

  //TODO: send relevant UnlikelyError (could be different than a Shopify error)
  // if (res.payload) throw new UnlikelyError('shopify', res.payload)

  return res
}

export const requestNextApiEndpoint = async (
  settings: EndpointSettings,
  params?: QueryParams,
) => {
  return await generateQuery({
    ...settings,
    ...(params ? params : {}),
  })
}

// Shopify
export const SHOPIFY_CUSTOMERS_UPDATE_LOCALE_PARAMS = {
  action: QueryRoutes.SHOPIFY_CUSTOMERS_UPDATE_LOCALE,
  method: QueryMethods.PUT,
}

export const SHOPIFY_CUSTOMERS_NEWSLETTER_PARAMS = {
  action: QueryRoutes.SHOPIFY_CUSTOMERS_NEWSLETTER,
  method: QueryMethods.POST,
}

export const SHOPIFY_ADMIN_ORDER_PARAMS = {
  action: QueryRoutes.SHOPIFY_ORDER_ADMIN,
  method: QueryMethods.GET,
}

// Stock Alert


// Wishlist
export async function getWishlistProfile(params: QueryParams) {
  return await generateQuery({
    ...params,
    action: QueryRoutes.WISHLIST_PROFILE,
    method: QueryMethods.GET,
  })
}

export function getWishlist(params: QueryParams) {
  return generateQuery({
    ...params,
    action: QueryRoutes.GET_WISHLIST,
    method: QueryMethods.POST,
  })
}

export async function updateWishlistProducts(params: QueryParams) {
  return await generateQuery({
    ...params,
    action: QueryRoutes.UPDATE_WISHLIST_PRODUCTS,
    method: QueryMethods.POST,
  })
}

export async function createWishlist(params: QueryParams) {
  return await generateQuery({
    ...params,
    action: QueryRoutes.CREATE_WISHLIST,
    method: QueryMethods.POST,
  })
}

export async function getPublicWishlist(params: QueryParams) {
  return await generateQuery({
    ...params,
    action: QueryRoutes.GET_PUBLIC_WISHLIST,
    method: QueryMethods.POST,
  })
}

// Back in stock - stock alert
export async function backInStockSubsribe(params: QueryParams) {
  return await generateQuery({
    ...params,
    action: QueryRoutes.BACK_IN_STOCK,
    method: QueryMethods.POST,
  })
}


// Google
export const GOOGLE_RECAPTCHA_PARAMS = {
  action: QueryRoutes.GOOGLE_RECAPTCHA,
  method: QueryMethods.POST,
}

export async function submitContactForm(params: QueryParams) {
  return await generateQuery({
    ...params,
    action: QueryRoutes.CONTACT,
    method: QueryMethods.POST,
  })
}

export const getGeolocation = async (params?: QueryParams) => {
  return await generateQuery({
    ...params,
    action: QueryRoutes.GEOLOCATION,
    method: QueryMethods.GET,
  })
}

export const getCountriesList = async (locale: string) => {
  return await generateQuery({
    action: QueryRoutes.COUNTRIES_LIST,
    method: QueryMethods.GET,
    queryParams: {
      locale,
    },
  })
}

export const getRecentlyViewedProducts = async (params: QueryParams) => {
  return await generateQuery({
    ...params,
    action: `${QueryRoutes.RECENTLY_VIEWED_PRODUCTS}?locale=${params?.payload?.locale}&uids=${params?.payload?.uids}`,
    method: QueryMethods.GET,
  })
}

export const getRecommendedProducts = async (params: QueryParams) => {
  return await generateQuery({
    // ...params,
    action: `${QueryRoutes.RECOMMENDED_PRODUCTS}?${params?.payload?.query}`,
    method: QueryMethods.GET,
  })
}
